/**
 * Navigation Settings
 */
import React from 'react';
import {
  SettingsGroup,
  SelectFieldSettings,
  TextSettings,
  ImageSettings,
} from '../components/settings';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';
import LinkPicker from '../../components/ui/LinkPicker';

// Хелпер для работы с текстовыми данными (строка или объект)
const getTextData = (value) => {
  if (typeof value === 'object' && value !== null) return value;
  return { text: value || '' };
};

export default function NavigationSettings({ data, onChange, allSections = [], allPages = [], projectId }) {
  const updateData = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const updateLogo = (field, value) => {
    onChange({
      ...data,
      logo: { ...data?.logo, [field]: value }
    });
  };

  const updateCtaButton = (field, value) => {
    onChange({
      ...data,
      ctaButton: { ...data?.ctaButton, [field]: value }
    });
  };

  // Управление ссылками
  const links = data?.links || [];

  const addLink = () => {
    const newLink = {
      id: `link-${Date.now()}`,
      text: 'Новая ссылка',
      href: '#',
      isPage: false
    };
    updateData('links', [...links, newLink]);
  };

  const updateLink = (id, field, value) => {
    const updated = links.map(link =>
      link.id === id ? { ...link, [field]: value } : link
    );
    updateData('links', updated);
  };

  const removeLink = (id) => {
    updateData('links', links.filter(link => link.id !== id));
  };

  const moveLink = (id, direction) => {
    const index = links.findIndex(link => link.id === id);
    if (index < 0) return;
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= links.length) return;

    const newLinks = [...links];
    [newLinks[index], newLinks[newIndex]] = [newLinks[newIndex], newLinks[index]];
    updateData('links', newLinks);
  };

  const logo = data?.logo || { type: 'text', text: '' };
  const ctaButton = data?.ctaButton || { enabled: true, text: '', href: '', variant: 'primary' };

  return (
    <div className="space-y-6">
      {/* Логотип */}
      <SettingsGroup title="Логотип">
        <SelectFieldSettings
          label="Тип"
          value={logo.type || 'text'}
          onChange={(value) => updateLogo('type', value)}
          options={[
            { value: 'text', label: 'Текст' },
            { value: 'image', label: 'Изображение' },
          ]}
        />

        {logo.type === 'text' ? (
          <TextSettings
            label="Название"
            value={getTextData(logo.text)}
            onChange={(value) => updateLogo('text', value)}
            placeholder="Название сайта"
          />
        ) : (
          <ImageSettings
            label="Изображение логотипа"
            value={logo.imageUrl}
            onChange={(value) => updateLogo('imageUrl', value)}
            showUpload={!!projectId}
            showMediaLibrary={!!projectId}
            projectId={projectId}
            previewHeight={60}
          />
        )}
      </SettingsGroup>

      {/* Ссылки навигации */}
      <SettingsGroup title="Меню навигации">
        <div className="space-y-2">
          {links.length === 0 ? (
            <p className="text-sm text-text-secondary py-2">
              Нет ссылок. Добавьте ссылки на страницы или внешние URL.
            </p>
          ) : (
            links.map((link, index) => (
              <div
                key={link.id}
                className="p-3 bg-muted rounded-lg space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => moveLink(link.id, -1)}
                      disabled={index === 0}
                      className="p-1 hover:bg-background rounded disabled:opacity-30"
                    >
                      <Icon name="ChevronUp" size={14} />
                    </button>
                    <button
                      onClick={() => moveLink(link.id, 1)}
                      disabled={index === links.length - 1}
                      className="p-1 hover:bg-background rounded disabled:opacity-30"
                    >
                      <Icon name="ChevronDown" size={14} />
                    </button>
                    <span className="text-sm font-medium ml-1">Пункт {index + 1}</span>
                  </div>
                  <button
                    onClick={() => removeLink(link.id)}
                    className="p-1 hover:bg-destructive/10 hover:text-destructive rounded"
                  >
                    <Icon name="Trash2" size={14} />
                  </button>
                </div>

                <TextSettings
                  label="Текст"
                  value={getTextData(link.text)}
                  onChange={(value) => updateLink(link.id, 'text', value)}
                  placeholder="Текст ссылки"
                />

                <LinkPicker
                  value={link.href}
                  onChange={(value) => updateLink(link.id, 'href', value)}
                  pages={allPages}
                  sections={allSections}
                  showLabel={false}
                />
              </div>
            ))
          )}
        </div>

        <button
          onClick={addLink}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors border border-dashed border-border"
        >
          <Icon name="Plus" size={16} />
          Добавить ссылку
        </button>

      </SettingsGroup>

      {/* Кнопка CTA */}
      <SettingsGroup title="Кнопка призыва">
        <Checkbox
          label="Показывать кнопку"
          checked={ctaButton.enabled !== false}
          onChange={(e) => updateCtaButton('enabled', e.target.checked)}
        />

        {ctaButton.enabled !== false && (
          <>
            <TextSettings
              label="Текст"
              value={getTextData(ctaButton.text)}
              onChange={(value) => updateCtaButton('text', value)}
              placeholder="Связаться"
            />

            <LinkPicker
              label="Ссылка"
              value={ctaButton.href || ''}
              onChange={(value) => updateCtaButton('href', value)}
              pages={allPages}
              sections={allSections}
            />

            <SelectFieldSettings
              label="Стиль"
              value={ctaButton.variant || 'primary'}
              onChange={(value) => updateCtaButton('variant', value)}
              options={[
                { value: 'primary', label: 'Основной' },
                { value: 'outline', label: 'Контурный' },
              ]}
            />
          </>
        )}
      </SettingsGroup>

      {/* Оформление */}
      <SettingsGroup title="Оформление">
        <SelectFieldSettings
          label="Стиль фона"
          value={data?.style || 'solid'}
          onChange={(value) => updateData('style', value)}
          options={[
            { value: 'solid', label: 'Сплошной' },
            { value: 'transparent', label: 'Прозрачный' },
            { value: 'blur', label: 'Размытие (glass)' },
          ]}
        />

        <SelectFieldSettings
          label="Выравнивание меню"
          value={data?.alignment || 'right'}
          onChange={(value) => updateData('alignment', value)}
          options={[
            { value: 'left', icon: 'AlignLeft' },
            { value: 'center', icon: 'AlignCenter' },
            { value: 'right', icon: 'AlignRight' },
          ]}
        />

        <Checkbox
          label="Закрепить при прокрутке"
          checked={data?.sticky !== false}
          onChange={(e) => updateData('sticky', e.target.checked)}
        />
      </SettingsGroup>
    </div>
  );
}
